import { useState, useMemo, useCallback, useEffect } from 'react';
import { isFormValid } from '../../utils/review/validationUtils';
import { hasFormData, getInitialFormState } from '../../utils/review/formUtils';
import { transformTaxonomyData } from '../../utils/review/taxonomyUtils';
import type { ReviewFormData } from '../../model/review/interfaces';
import { useProductService } from '../product/useProductService';
import { useUserService } from '../user/useUserService';

interface UseReviewFormOptions {
  isOpen: boolean;
}

interface UseReviewFormReturn {
  formState: ReviewFormData;
  formActions: {
    setRating: (value: number) => void;
    setTitle: (value: string) => void;
    setContent: (value: string) => void;
    setNickname: (value: string) => void;
    setEmail: (value: string) => void;
    setSkinType: (value: string) => void;
    setSkinConcern: (value: string) => void;
    setImages: (value: File[]) => void;
    setRecommend: (value: string) => void;
  };
  formOptions: {
    skinTypeOptions: Array<{ value: string; label: string }>;
    skinConcernOptions: Array<{ value: string; label: string }>;
  };
  hasUnsavedChanges: boolean;
  isSubmitDisabled: boolean;
  resetForm: () => void;
}

export const useReviewForm = ({ isOpen }: UseReviewFormOptions): UseReviewFormReturn => {
  const [formState, setFormState] = useState<ReviewFormData>(getInitialFormState());
  const { useGetProductTaxonomy } = useProductService();

  const { data: taxonomy } = useGetProductTaxonomy({
    enabled: isOpen,
  });

  const { skinTypeOptions, skinConcernOptions } = transformTaxonomyData(taxonomy);
  const { useGetUserMe } = useUserService();
  const { data: user } = useGetUserMe();

  useEffect(() => {
    if (user) {
      setFormState((prev) => ({ ...prev, nickname: user.userName }));
      setFormState((prev) => ({ ...prev, email: user.email }));
    }
  }, [user]);

  // Form actions for cleaner prop passing
  const formActions = useMemo(
    () => ({
      setRating: (value: number) => setFormState((prev) => ({ ...prev, rating: value })),
      setTitle: (value: string) => setFormState((prev) => ({ ...prev, title: value })),
      setContent: (value: string) => setFormState((prev) => ({ ...prev, content: value })),
      setNickname: (value: string) => setFormState((prev) => ({ ...prev, nickname: value })),
      setEmail: (value: string) => setFormState((prev) => ({ ...prev, email: value })),
      setSkinType: (value: string) => setFormState((prev) => ({ ...prev, skinType: value })),
      setSkinConcern: (value: string) => setFormState((prev) => ({ ...prev, skinConcern: value })),
      setImages: (value: File[]) => setFormState((prev) => ({ ...prev, images: value })),
      setRecommend: (value: string) => setFormState((prev) => ({ ...prev, recommend: value })),
    }),
    [],
  );

  const formOptions = {
    skinTypeOptions,
    skinConcernOptions,
  };

  // Derived state
  const hasUnsavedChanges = hasFormData(formState);
  const isSubmitDisabled = !isFormValid(formState);

  // Reset form utility
  const resetForm = useCallback(() => {
    setFormState(getInitialFormState());
  }, []);

  return {
    formState,
    formActions,
    formOptions,
    hasUnsavedChanges,
    isSubmitDisabled,
    resetForm,
  };
};
